<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition ease-in duration-150"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div v-if="modelValue" class="fixed inset-0 z-50 flex items-center justify-center p-4">
        <!-- Backdrop -->
        <div class="absolute inset-0 bg-black/50" @click="close" />

        <!-- Modal -->
        <div class="relative bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
          <!-- Header -->
          <div class="p-4 border-b border-gray-200">
            <div class="flex items-center justify-between">
              <h3 class="text-lg font-semibold text-gray-900">Grade Details</h3>
              <button
                class="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                @click="close"
              >
                <font-awesome-icon :icon="['fas', 'xmark']" class="text-lg" />
              </button>
            </div>

            <!-- Student & Assignment info -->
            <div class="mt-3 flex items-center gap-3">
              <StudentAvatar :student="student" size="md" :color-index="0" />
              <div>
                <p class="font-medium text-gray-900">{{ getStudentDisplayName(student) }}</p>
                <p class="text-sm text-gray-500">{{ assignmentName }}</p>
              </div>
            </div>
          </div>

          <!-- Body - Grade display -->
          <div class="p-6">
            <!-- Grade display -->
            <div class="text-center mb-6">
              <div
                class="inline-flex items-center justify-center w-24 h-24 rounded-2xl text-3xl font-bold mb-3"
                :class="gradeColorClass"
              >
                {{ formattedGrade }}
              </div>
              <p v-if="percentageText" class="text-gray-500">{{ percentageText }}</p>
            </div>

            <!-- Marking type -->
            <div class="flex items-center justify-center gap-2 text-sm text-gray-500 mb-4">
              <font-awesome-icon :icon="['fas', markingIcon]" />
              <span>{{ markingTypeLabel }}</span>
            </div>

            <!-- Marked date -->
            <div v-if="submission?.markedAt" class="text-center text-sm text-gray-400">
              Recorded {{ formatDate(submission.markedAt) }}
            </div>

            <!-- Comments -->
            <div v-if="submission?.comments?.length" class="mt-6 p-4 bg-gray-50 rounded-xl">
              <div class="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                <font-awesome-icon :icon="['fas', 'comment']" />
                <span>Comment</span>
              </div>
              <p class="text-sm text-gray-600">
                {{ submission.comments[0]?.body }}
              </p>
            </div>
          </div>

          <!-- Footer -->
          <div class="p-4 border-t border-gray-200 flex items-center justify-between">
            <AppButton
              variant="primary"
              size="md"
              :loading="deleting"
              class="!bg-transparent !text-red-600 hover:!bg-red-50"
              @click="handleDelete"
            >
              <font-awesome-icon :icon="['fas', 'trash']" />
              <span>Remove</span>
            </AppButton>

            <AppButton variant="primary" size="md" @click="handleEdit">
              <font-awesome-icon :icon="['fas', 'pencil']" />
              <span>Edit Grade</span>
            </AppButton>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import {
  gradebookApi,
  type GradebookStudent,
  type GradebookSubmission,
  type MarkingType,
} from '@/api'
import { getStudentDisplayName } from '@/utils/student'
import StudentAvatar from '@/components/StudentAvatar.vue'
import { AppButton } from '@/design-system/components'

const props = defineProps<{
  modelValue: boolean
  student: GradebookStudent | null
  submission: GradebookSubmission | null
  assignmentName?: string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  edit: []
  deleted: []
}>()

const deleting = ref(false)

const markingType = computed<MarkingType | null>(() => {
  return props.submission?.marking?.type ?? null
})

const formattedGrade = computed(() => {
  const marking = props.submission?.marking
  if (!marking) return '-'

  switch (marking.type) {
    case 'INTEGER':
    case 'DECIMAL':
      return `${marking.score}/${marking.maxPoints}`
    case 'PERCENT':
      return `${Math.round(marking.percent ?? 0)}%`
    case 'SCALE':
      return marking.level?.text ?? '-'
    default:
      return '-'
  }
})

const percentageText = computed(() => {
  const marking = props.submission?.marking
  if (!marking) return null

  // For points, show percentage
  if (marking.type === 'INTEGER' || marking.type === 'DECIMAL') {
    if (marking.score != null && marking.maxPoints) {
      const percent = Math.round((marking.score / marking.maxPoints) * 100)
      return `${percent}%`
    }
  }
  return null
})

const gradeColorClass = computed(() => {
  const grade = props.submission?.grade
  if (grade == null) return 'bg-gray-100 text-gray-600'

  const percent = grade * 100
  if (percent >= 90) return 'bg-emerald-100 text-emerald-700'
  if (percent >= 75) return 'bg-blue-100 text-blue-700'
  if (percent >= 60) return 'bg-amber-100 text-amber-700'
  return 'bg-red-100 text-red-700'
})

const markingIcon = computed(() => {
  switch (markingType.value) {
    case 'INTEGER':
    case 'DECIMAL':
      return 'hashtag'
    case 'PERCENT':
      return 'percent'
    case 'SCALE':
      return 'list-ol'
    default:
      return 'check'
  }
})

const markingTypeLabel = computed(() => {
  switch (markingType.value) {
    case 'INTEGER':
      return 'Points (whole numbers)'
    case 'DECIMAL':
      return 'Points (decimals allowed)'
    case 'PERCENT':
      return 'Percentage'
    case 'SCALE':
      return 'Scale grade'
    default:
      return 'Grade'
  }
})

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  } catch {
    return dateStr
  }
}

function close() {
  emit('update:modelValue', false)
}

function handleEdit() {
  emit('edit')
}

async function handleDelete() {
  if (!props.submission) return

  deleting.value = true

  try {
    await gradebookApi.deleteSubmission(props.submission.id)
    emit('deleted')
    close()
  } catch (err: unknown) {
    console.error('Failed to delete grade:', err)
  } finally {
    deleting.value = false
  }
}
</script>
